import { NgIf, NgFor } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatSelectChange, MatSelect, MatOption } from '@angular/material/select';
import { RootState } from '@next/core-lib/app';
import { SummaryCardComponent } from '@next/core-lib/components';
import { TranslatePipe } from '@next/core-lib/i18n';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import {
  Calculation,
  CountryOptions,
  FormValues,
  GenderOptions,
} from 'src/app/feed-profit/models/feed-profit.model';

import * as actions from '../../../../store/feed-profit.actions';
import { BaselineFeedersComponent } from '../../../shared/baseline-feeders/baseline-feeders.component';

@Component({
  selector: 'app-products',
  templateUrl: './products.component.html',
  styleUrls: ['./products.component.scss'],
  imports: [
    NgIf,
    SummaryCardComponent,
    ReactiveFormsModule,
    MatSelect,
    NgFor,
    MatOption,
    BaselineFeedersComponent,
    TranslatePipe,
  ],
})
export class ProductsComponent implements OnDestroy, OnInit {
  @Input() calculation: Calculation;

  @ViewChild(BaselineFeedersComponent) baselineFeedersComponent: BaselineFeedersComponent;

  @Output()
  productsChanged: EventEmitter<Calculation> = new EventEmitter<Calculation>();

  formSubscription: Subscription;

  form: FormGroup = new FormGroup({
    genders: new FormControl(''),
    country: new FormControl(''),
  });

  genderOptions: string[] = Object.values(GenderOptions);
  countryOptions: string[] = Object.values(CountryOptions);

  constructor(private readonly store: Store<RootState>) {}

  ngOnInit(): void {
    this.setProductsValues(this.calculation);

    this.formSubscription = this.form.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe((values: FormValues) => {
        const genders =
          values.genders === 'barrow_gilt' ? GenderOptions.BARROW_GILT : GenderOptions.BOAR_GILT;

        const country =
          values.country === 'nl'
            ? CountryOptions.NL
            : values.country === 'du'
              ? CountryOptions.DU
              : values.country === 'bio'
                ? CountryOptions.BIO
                : null;
        this.updateProducts({
          ...this.calculation,
          extendedSimulation: {
            ...this.calculation.extendedSimulation,
            country,
            genders,
          },
        });
        this.changeGenderSelection({ value: genders });
      });
  }

  setProductsValues(calculation: Calculation) {
    if (calculation) {
      const genders =
        calculation.extendedSimulation?.genders === 'barrow_gilt'
          ? GenderOptions.BARROW_GILT
          : GenderOptions.BOAR_GILT;
      this.form.controls.genders.setValue(genders);
      this.form.controls.country.setValue(calculation.extendedSimulation?.country ?? '');
    }
  }

  ngOnDestroy(): void {
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }
  }

  updateProducts(calculation: Calculation) {
    this.baselineFeedersComponent?.updateCalculation(calculation);
    this.baselineFeedersComponent?.changeGenderSelection(
      calculation.extendedSimulation?.genders || GenderOptions.BOAR_GILT,
    );
    this.productsChanged.emit(calculation);
  }

  updateFeeders(calculation: Calculation) {
    this.productsChanged.emit(calculation);
  }

  changeGenderSelection($event: MatSelectChange | { value: string }) {
    const genders =
      $event.value === 'barrow_gilt' ? GenderOptions.BARROW_GILT : GenderOptions.BOAR_GILT;

    this.baselineFeedersComponent?.changeGenderSelection(<string>$event.value);
    if (this.form.controls.genders.value !== genders) {
      this.form.controls.genders.setValue(genders, {
        emitEvent: false,
      });
    }
    this.store.dispatch(actions.setIsChanged({ isChanged: true }));
  }
}
